import { useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useCreativeProject } from "../../hooks/creative/useCreativeProjects";
import { StudioCarousel } from "../../pages/StudioProfile/StudioGallery/StudioCarousel";
import classes from "./VisualProjectView.module.css";

export const VisualProjectView = ({ creative }) => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { project, loading, error } = useCreativeProject(projectId);
  const [showCarousel, setShowCarousel] = useState(false);
  const [carouselStartIndex, setCarouselStartIndex] = useState(0);

  const handleBack = () => {
    navigate(`/creative-editing/${creative?.id}`);
  };

  const handleImageClick = (index) => {
    setCarouselStartIndex(index);
    setShowCarousel(true);
  };

  const getImageUrls = () => {
    if (!project?.processedImages || project.processedImages.length === 0) return [];
    
    return project.processedImages.map(img => {
      if (typeof img === 'string') return img;
      return img.processedUrl || img.url || img;
    }).filter(Boolean);
  };

  const getMediaItems = () => {
    if (!project?.images || project.images.length === 0) return [];
    
    return project.images.map((item, index) => {
      const isLink = item.type === 'link' || 
        (item.url && typeof item.url === 'string' && 
         (item.url.includes('youtube.com') || item.url.includes('youtu.be') || item.url.includes('vimeo.com')));
      const isVideo = item.type === 'video';
      
      let displayUrl = '';
      if (isLink && item.thumbnailUrl) {
        displayUrl = item.thumbnailUrl;
      } else if (isVideo && item.thumbnailUrl) {
        displayUrl = item.thumbnailUrl;
      } else if (item.url) {
        const processedImg = project.processedImages?.[index];
        displayUrl = processedImg?.processedUrl || processedImg?.url || item.url;
      }
      
      return {
        ...item,
        displayUrl,
        isLink,
        isVideo,
        isPhoto: !isLink && !isVideo
      };
    });
  };

  if (loading) {
    return <div className={classes.container}>Wird geladen...</div>;
  }

  if (error || !project) {
    return (
      <div className={classes.container}>
        <p>Projekt wurde nicht gefunden.</p>
        <button onClick={handleBack}>Zurück</button>
      </div>
    );
  }

  const mediaItems = getMediaItems();
  const imageUrls = getImageUrls();
  const studio = project.studio;

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <button className={classes.backButton} onClick={handleBack}>
          <svg xmlns="http://www.w3.org/2000/svg" width="11" height="20" viewBox="0 0 11 20" fill="none">
            <path fillRule="evenodd" clipRule="evenodd" d="M0 9.6255L8.99855 0.626953L10.2006 1.82903L2.35773 9.67193L10.2486 18.2204L8.99941 19.3734L0.0172915 9.64279L0 9.6255Z" fill="#1F1F1F"/>
          </svg>
        </button>
        <h1 className="titleSemibold2">{project.title}</h1>
      </div>

      {project.description && (
        <div className={classes.descriptionSection}>
          <h2 className="semiboldLarge">Projektbeschreibung</h2>
          <p className="regularLarge">{project.description}</p>
        </div>
      )}

      {mediaItems.length > 0 && (
        <div className={classes.gallerySection}>
          <h2 className="semiboldLarge">Galerie</h2>
          <div className={classes.galleryGrid}>
            {mediaItems.map((item, index) => (
              <div 
                key={index} 
                className={classes.galleryItem}
                onClick={() => !item.isLink && handleImageClick(index)}
              >
                {item.isLink && (
                  <div className={classes.linkBadge}>
                    <span>🔗</span>
                  </div>
                )}
                {item.isVideo && !item.isLink && (
                  <div className={classes.videoBadge}>
                    <span>🎥</span>
                  </div>
                )}
                {item.displayUrl ? (
                  <img src={item.displayUrl} alt={`${project.title} ${index + 1}`} />
                ) : item.isVideo ? (
                  <div className={classes.videoPlaceholder}>
                    <span>🎥</span>
                    <p>Video</p>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      )}

      {studio && (
        <Link to={`/studio/${studio.id}`} className={classes.studioCard}>
          {studio.processedImages?.[0] && (
            <div className={classes.studioImage}>
              <img src={studio.processedImages[0]} alt={studio.StudioName} />
            </div>
          )}
          <div className={classes.studioInfo}>
            <p className="mediumLarge">{studio.StudioName}</p>
            {studio.city && (
              <p style={{color: "var(--dark-grey-3)"}} className="slimRegularSmallText">
                {studio.city}{studio.country ? `, ${studio.country}` : ''}
              </p>
            )}
          </div>
        </Link>
      )}

      {creative && (
        <Link 
          to={`/creative-editing/${creative.id}/project/${project.id}`}
          className={classes.editButton}
        >
          Bearbeiten
        </Link>
      )}

      {showCarousel && imageUrls.length > 0 && (
        <StudioCarousel 
          images={imageUrls} 
          startIndex={carouselStartIndex} 
          onClose={() => setShowCarousel(false)}
        />
      )}
    </div>
  );
};

export default VisualProjectView;
